import * as THREE from 'three';
import { ARENA, DEATH_FX } from '../../../core/config/gameConfig';
import type { DebrisPhysics, ShardBody } from './debrisPhysics';

export interface ShatterOptions {
  impulse?: number;
  /** World-space direction of the killing shot — debris sprays away from it. */
  impactDir?: { x: number; y: number; z: number };
  /** Fragment look: glass shards get translucency and an edge glint. */
  kind?: 'metal' | 'glass';
  /** Fragment color; falls back to the mesh material's color. */
  color?: number;
}

// All fragments from one death share materials; disposed when the last one dies.
interface Batch {
  shardMat: THREE.MeshStandardMaterial;
  edgeMat: THREE.LineBasicMaterial | null;
  count: number;
}

interface Fragment {
  mesh: THREE.Mesh;
  /** Additive edge glint (glass only); child of mesh, fades then detaches. */
  edges: THREE.LineSegments | null;
  edgeLife: number;
  velocity: THREE.Vector3;
  angular: THREE.Vector3;
  life: number;
  radius: number;
  /** Counts down once crumbling starts; fragment removed at 0. */
  crumble: number;
  batch: Batch;
  /** Rapier rigid body when debris physics is on; null = integrator fallback. */
  body: ShardBody | null;
}

/** Cap on live rigid bodies — kill streaks degrade to the integrator, never
 *  to a frame-rate cliff. */
const MAX_PHYSICS_BODIES = 80;

interface Grain {
  life: number;
  initial: number;
  size: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
}

const EDGE_GLINT_SEC = 0.4;
const DUST_MAX = 512;

// 14 fixed cluster directions: the 6 axes plus the 8 octant diagonals. Every
// triangle joins the fragment whose direction best matches its own direction
// from the shape's center — deterministic, and spatially coherent for any
// geometry (extruded glyphs, torus, stellated spikes) without knowing anything
// about the shape.
const CLUSTER_DIRECTIONS: THREE.Vector3[] = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
  new THREE.Vector3(1, 1, 1).normalize(),
  new THREE.Vector3(1, 1, -1).normalize(),
  new THREE.Vector3(1, -1, 1).normalize(),
  new THREE.Vector3(1, -1, -1).normalize(),
  new THREE.Vector3(-1, 1, 1).normalize(),
  new THREE.Vector3(-1, 1, -1).normalize(),
  new THREE.Vector3(-1, -1, 1).normalize(),
  new THREE.Vector3(-1, -1, -1).normalize(),
];

// Death effect: the mesh is carved into sealed chunks of its own surface which
// spray away from the shot, tumble, knock into each other, land, and crumble
// into dust. Runs entirely in the render layer.
//
// Contract: shatter() removes the mesh from the scene graph but disposes
// NOTHING of it — geometries may be shared templates (see shapeFactory /
// textFactory) and the caller owns the per-instance material.
export class ShapeShatter {
  private fragments: Fragment[] = [];
  private grains: Grain[] = [];
  private dust: THREE.InstancedMesh | null = null;
  private readonly dustDummy = new THREE.Object3D();
  private physics: DebrisPhysics | null = null;

  constructor(private readonly scene: THREE.Scene) {}

  /** Enable/disable rigid-body debris. Existing shards fall back gracefully. */
  setPhysics(physics: DebrisPhysics | null): void {
    if (!physics && this.physics) {
      for (const f of this.fragments) {
        if (f.body) {
          this.physics.remove(f.body);
          f.body = null;
        }
      }
      this.physics.dispose();
    }
    this.physics = physics;
  }

  shatter(mesh: THREE.Mesh, opts: ShatterOptions = {}): void {
    const impulse = opts.impulse ?? DEATH_FX.impulse;
    const kind = opts.kind ?? 'metal';

    // Work on a clone baked into world space — the source geometry may be a
    // shared template that must never be mutated or disposed.
    mesh.updateWorldMatrix(true, false);
    const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
    geometry.applyMatrix4(mesh.matrixWorld);
    geometry.computeBoundingSphere();
    const center = geometry.boundingSphere!.center;

    // Bucket triangles by which fixed direction their centroid points toward.
    const positions = geometry.getAttribute('position');
    const clusters: number[][] = CLUSTER_DIRECTIONS.map(() => []);
    const centroid = new THREE.Vector3();
    const vertex = new THREE.Vector3();
    for (let tri = 0; tri < positions.count / 3; tri++) {
      centroid.set(0, 0, 0);
      for (let v = 0; v < 3; v++) {
        centroid.add(vertex.fromBufferAttribute(positions, tri * 3 + v));
      }
      centroid.divideScalar(3).sub(center);
      if (centroid.lengthSq() < 1e-8) centroid.set(0, 1, 0);
      centroid.normalize();
      let best = 0;
      let bestDot = -Infinity;
      for (let d = 0; d < CLUSTER_DIRECTIONS.length; d++) {
        const dot = centroid.dot(CLUSTER_DIRECTIONS[d]);
        if (dot > bestDot) {
          bestDot = dot;
          best = d;
        }
      }
      clusters[best].push(tri);
    }

    const sourceMat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const color = new THREE.Color(
      opts.color ?? (sourceMat as THREE.MeshStandardMaterial).color?.getHex() ?? 0xffffff,
    );
    const batch = this.createBatch(color, kind);

    const impactDir = opts.impactDir
      ? new THREE.Vector3(opts.impactDir.x, opts.impactDir.y, opts.impactDir.z).normalize()
      : null;

    for (const tris of clusters) {
      if (tris.length === 0) continue;
      this.spawnFragment(tris, positions, center, batch, impulse, impactDir, kind);
    }
    if (batch.count === 0) this.disposeBatch(batch);

    mesh.removeFromParent();
    geometry.dispose(); // only the working clone
  }

  private createBatch(color: THREE.Color, kind: 'metal' | 'glass'): Batch {
    const glass = kind === 'glass';
    const shardMat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: glass ? 0.35 : 0.25,
      metalness: glass ? 0.1 : 0.5,
      roughness: glass ? 0.15 : 0.45,
      transparent: glass,
      opacity: glass ? 0.72 : 1,
      side: THREE.DoubleSide,
    });
    // Broken-glass edge glint: whitened digit color, additive, brief.
    const edgeMat = glass
      ? new THREE.LineBasicMaterial({
          color: color.clone().lerp(new THREE.Color(0xffffff), 0.65),
          transparent: true,
          opacity: 0.9,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      : null;
    return { shardMat, edgeMat, count: 0 };
  }

  private disposeBatch(batch: Batch): void {
    batch.shardMat.dispose();
    batch.edgeMat?.dispose();
  }

  // Builds one sealed fragment: the cluster's surface triangles plus cap fans
  // over its boundary edges toward an interior point, so shards read as solid
  // chunks instead of hollow shells. Geometry is recentered on the cluster
  // centroid so tumbling rotates about the fragment's own middle.
  private spawnFragment(
    tris: number[],
    positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    center: THREE.Vector3,
    batch: Batch,
    impulse: number,
    impactDir: THREE.Vector3 | null,
    kind: 'metal' | 'glass',
  ): void {
    const centroid = new THREE.Vector3();
    const vertex = new THREE.Vector3();

    // Surface triangles + boundary-edge bookkeeping (edges seen once = rim).
    const surface: number[] = [];
    const edgeUse = new Map<string, { a: THREE.Vector3; b: THREE.Vector3; n: number }>();
    const key = (v: THREE.Vector3): string =>
      `${Math.round(v.x * 1e4)},${Math.round(v.y * 1e4)},${Math.round(v.z * 1e4)}`;
    const corners = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    for (const tri of tris) {
      for (let v = 0; v < 3; v++) {
        vertex.fromBufferAttribute(positions, tri * 3 + v);
        corners[v].copy(vertex);
        centroid.add(vertex);
        surface.push(vertex.x, vertex.y, vertex.z);
      }
      for (let v = 0; v < 3; v++) {
        const a = corners[v];
        const b = corners[(v + 1) % 3];
        const ka = key(a);
        const kb = key(b);
        const ek = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        const entry = edgeUse.get(ek);
        if (entry) entry.n++;
        else edgeUse.set(ek, { a: a.clone(), b: b.clone(), n: 1 });
      }
    }
    centroid.divideScalar(tris.length * 3);

    // Interior point: the centroid pulled toward the shape center — caps fan
    // to it, closing the shard into a rough solid wedge.
    const inner = centroid.clone().lerp(center, 0.35);
    const verts: number[] = [...surface];
    for (const { a, b, n } of edgeUse.values()) {
      if (n !== 1) continue; // interior edge, already sealed by a neighbor tri
      verts.push(a.x, a.y, a.z, b.x, b.y, b.z, inner.x, inner.y, inner.z);
    }

    const array = new Float32Array(verts);
    for (let i = 0; i < array.length; i += 3) {
      array[i] -= centroid.x;
      array[i + 1] -= centroid.y;
      array[i + 2] -= centroid.z;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(array, 3));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();

    const fragment = new THREE.Mesh(geometry, batch.shardMat);
    fragment.position.copy(centroid);
    batch.count++;

    const angular = new THREE.Vector3(
      (Math.random() - 0.5) * 5,
      (Math.random() - 0.5) * 5,
      (Math.random() - 0.5) * 5,
    );

    let edges: THREE.LineSegments | null = null;
    if (batch.edgeMat && kind === 'glass') {
      edges = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 20), batch.edgeMat);
      fragment.add(edges);
    }

    // Direction: radial burst blended toward the shot direction, so debris
    // sprays away from the player's hit instead of symmetrically.
    const outward = centroid.clone().sub(center);
    if (outward.lengthSq() < 1e-8) outward.set(0, 1, 0);
    outward.normalize();
    const dir = impactDir
      ? outward.multiplyScalar(1 - DEATH_FX.impactBias).addScaledVector(impactDir, DEATH_FX.impactBias).normalize()
      : outward;
    const speed = impulse * (0.7 + Math.random() * 0.6);
    const velocity = dir.multiplyScalar(speed);
    velocity.y += 1.0;

    let body: ShardBody | null = null;
    if (this.physics && this.physics.bodyCount() < MAX_PHYSICS_BODIES) {
      body = this.physics.addShard(array, centroid, velocity, angular);
    }

    this.fragments.push({
      mesh: fragment,
      edges,
      edgeLife: EDGE_GLINT_SEC,
      velocity,
      angular,
      life: body ? DEATH_FX.fragmentLife * 1.6 : DEATH_FX.fragmentLife,
      radius: geometry.boundingSphere!.radius,
      crumble: -1,
      batch,
      body,
    });
    this.scene.add(fragment);
  }

  update(dt: number): void {
    const { gravity, restitution, friction, crumbleSec } = DEATH_FX;
    const ground = ARENA.groundY;

    // One physics step serves every rigid-body shard this frame.
    if (this.physics && this.fragments.some((f) => f.body)) this.physics.step(dt);

    for (let i = this.fragments.length - 1; i >= 0; i--) {
      const f = this.fragments[i];

      // Crumble stage: shrink into dust, then vanish.
      if (f.crumble >= 0) {
        f.crumble -= dt;
        if (f.crumble <= 0) {
          this.removeFragment(i);
          continue;
        }
        const k = f.crumble / crumbleSec;
        f.mesh.scale.setScalar(0.25 + 0.75 * k);
        continue;
      }

      f.life -= dt;
      if (f.life <= 0) {
        this.startCrumble(f);
        continue;
      }

      // Edge glint fades fast, then the line overlay detaches entirely.
      if (f.edges) {
        f.edgeLife -= dt;
        if (f.edgeLife <= 0) {
          f.mesh.remove(f.edges);
          f.edges.geometry.dispose();
          f.edges = null;
        }
      }

      // Rigid-body path: Rapier owns motion, ground bounces, and shard-vs-
      // shard contacts; we just mirror the transform and watch for sleep.
      if (f.body && this.physics) {
        if (!this.physics.readTransform(f.body, f.mesh.position, f.mesh.quaternion)) {
          f.body = null;
        } else {
          if (this.physics.isResting(f.body)) this.startCrumble(f);
          continue;
        }
      }

      f.mesh.position.x += f.velocity.x * dt;
      f.mesh.position.y += f.velocity.y * dt;
      f.mesh.position.z += f.velocity.z * dt;
      f.velocity.y -= gravity * dt;
      f.mesh.rotation.x += f.angular.x * dt;
      f.mesh.rotation.y += f.angular.y * dt;
      f.mesh.rotation.z += f.angular.z * dt;

      // Rest height uses a fraction of the bounding-sphere radius: flat plate
      // fragments have big spheres but tiny thickness, and full-radius resting
      // would leave them hovering above the floor.
      const restHeight = Math.max(0.05, f.radius * 0.4);
      if (f.mesh.position.y < ground + restHeight) {
        f.mesh.position.y = ground + restHeight;
        if (Math.abs(f.velocity.y) > 0.6) {
          f.velocity.y = -f.velocity.y * restitution;
          f.velocity.x *= friction;
          f.velocity.z *= friction;
          f.angular.multiplyScalar(0.6);
        } else {
          // Landed: crumble where it lies.
          this.startCrumble(f);
        }
      }
    }

    if (DEATH_FX.shardCollisions) this.collideFragments();
    this.updateDust(dt, gravity, ground);
  }

  private startCrumble(f: Fragment): void {
    f.crumble = DEATH_FX.crumbleSec;
    if (f.body && this.physics) {
      this.physics.remove(f.body);
      f.body = null;
    }
    if (f.edges) {
      f.mesh.remove(f.edges);
      f.edges.geometry.dispose();
      f.edges = null;
    }
    this.emitDust(f.mesh.position, f.batch.shardMat.color, f.radius);
  }

  private removeFragment(index: number): void {
    const f = this.fragments[index];
    if (f.body && this.physics) this.physics.remove(f.body);
    this.scene.remove(f.mesh);
    f.mesh.geometry.dispose();
    if (f.edges) f.edges.geometry.dispose();
    f.batch.count--;
    if (f.batch.count <= 0) this.disposeBatch(f.batch);
    this.fragments.splice(index, 1);
  }

  // One cheap sphere-sphere impulse pass so debris knocks into other debris.
  private collideFragments(): void {
    const n = this.fragments.length;
    if (n < 2 || n > 48) return;
    for (let i = 0; i < n; i++) {
      const a = this.fragments[i];
      if (a.crumble >= 0 || a.body) continue; // Rapier shards collide for real
      for (let j = i + 1; j < n; j++) {
        const b = this.fragments[j];
        if (b.crumble >= 0 || b.body) continue;
        const dx = b.mesh.position.x - a.mesh.position.x;
        const dy = b.mesh.position.y - a.mesh.position.y;
        const dz = b.mesh.position.z - a.mesh.position.z;
        const minDist = (a.radius + b.radius) * 0.7;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq >= minDist * minDist || distSq < 1e-10) continue;
        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;
        const nz = dz / dist;
        // Separate the overlap, then swap the closing velocity along the normal.
        const push = (minDist - dist) * 0.5;
        a.mesh.position.x -= nx * push;
        a.mesh.position.y -= ny * push;
        a.mesh.position.z -= nz * push;
        b.mesh.position.x += nx * push;
        b.mesh.position.y += ny * push;
        b.mesh.position.z += nz * push;
        const rel =
          (b.velocity.x - a.velocity.x) * nx +
          (b.velocity.y - a.velocity.y) * ny +
          (b.velocity.z - a.velocity.z) * nz;
        if (rel < 0) {
          const impulse = -rel * 0.6;
          a.velocity.x -= nx * impulse;
          a.velocity.y -= ny * impulse;
          a.velocity.z -= nz * impulse;
          b.velocity.x += nx * impulse;
          b.velocity.y += ny * impulse;
          b.velocity.z += nz * impulse;
          a.angular.multiplyScalar(0.9);
          b.angular.multiplyScalar(0.9);
        }
      }
    }
  }

  // ---- Dust: one shared InstancedMesh, grains live ~a second ----

  private ensureDust(): THREE.InstancedMesh {
    if (this.dust) return this.dust;
    const geo = new THREE.TetrahedronGeometry(0.035);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const mesh = new THREE.InstancedMesh(geo, mat, DUST_MAX);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    this.dust = mesh;
    return mesh;
  }

  private emitDust(origin: THREE.Vector3, color: THREE.Color, radius: number): void {
    const dust = this.ensureDust();
    for (let i = 0; i < DEATH_FX.dustPerShard; i++) {
      if (this.grains.length >= DUST_MAX) break;
      const life = 0.6 + Math.random() * 0.5;
      this.grains.push({
        life,
        initial: life,
        size: 0.6 + Math.random() * 0.8,
        position: origin
          .clone()
          .add(
            new THREE.Vector3(
              (Math.random() - 0.5) * radius,
              Math.random() * radius * 0.5,
              (Math.random() - 0.5) * radius,
            ),
          ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 2.2,
          1.2 + Math.random() * 1.8,
          (Math.random() - 0.5) * 2.2,
        ),
      });
      const idx = this.grains.length - 1;
      dust.setColorAt(idx, color);
    }
    if (dust.instanceColor) dust.instanceColor.needsUpdate = true;
  }

  private updateDust(dt: number, gravity: number, ground: number): void {
    if (!this.dust || this.grains.length === 0) {
      if (this.dust) this.dust.count = 0;
      return;
    }
    for (let i = this.grains.length - 1; i >= 0; i--) {
      const g = this.grains[i];
      g.life -= dt;
      if (g.life <= 0) {
        // Swap-remove; keep colors in sync with the swapped grain.
        const last = this.grains.length - 1;
        if (i !== last) {
          this.grains[i] = this.grains[last];
          const c = new THREE.Color();
          this.dust.getColorAt(last, c);
          this.dust.setColorAt(i, c);
        }
        this.grains.pop();
        continue;
      }
      g.position.addScaledVector(g.velocity, dt);
      g.velocity.y -= gravity * 0.6 * dt;
      if (g.position.y < ground + 0.03) {
        g.position.y = ground + 0.03;
        g.velocity.y = 0;
        g.velocity.x *= 0.85;
        g.velocity.z *= 0.85;
      }
    }
    for (let i = 0; i < this.grains.length; i++) {
      const g = this.grains[i];
      const s = g.size * (g.life / g.initial);
      this.dustDummy.position.copy(g.position);
      this.dustDummy.rotation.set(g.life * 5, g.life * 7, 0);
      this.dustDummy.scale.setScalar(Math.max(s, 1e-4));
      this.dustDummy.updateMatrix();
      this.dust.setMatrixAt(i, this.dustDummy.matrix);
    }
    this.dust.count = this.grains.length;
    this.dust.instanceMatrix.needsUpdate = true;
    if (this.dust.instanceColor) this.dust.instanceColor.needsUpdate = true;
  }

  clear(): void {
    for (let i = this.fragments.length - 1; i >= 0; i--) this.removeFragment(i);
    this.grains.length = 0;
    if (this.dust) this.dust.count = 0;
  }
}
